import { Textarea } from "@headlessui/react";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { clsx } from "@/utils";
import { Typography } from "..";

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  hideCharCount?: boolean;
  hasError?: boolean;
}

/**
 * The countdown label, exported so a caller that has to place the count itself renders the same words.
 *
 * `EditRowModal` is the one such caller: its design puts the count on the same line as the field's
 * "Optional" hint, hard right, which cannot be done from inside this component's own wrapper. Sharing
 * the string keeps the two from drifting if the wording ever changes.
 */
export const charactersLeftLabel = (maxLength: number, length: number) => `${Math.max(maxLength - length, 0)} characters left`;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>((textAreaProps, ref) => {
  const { maxLength, className, rows = 4, hideCharCount = false, hasError = false, ...props } = textAreaProps;

  /**
   * Length of what's in the field right now.
   *
   * Only consulted when this component is UNCONTROLLED. A controlled caller passes `value`, which is
   * always the truth and needs no state at all.
   *
   * It used to be a plain `useState(0)` updated on input, for both cases. That made the count wrong on
   * every field opened with something already in it — "2048 characters left" beside a full box of admin
   * notes — and it stayed wrong until the first keystroke. Every edit form bound to an existing record
   * had it.
   */
  const [uncontrolledLength, setUncontrolledLength] = useState(0);

  /**
   * Reads the initial length off the DOM node once it exists, then hands the node to whatever ref the
   * caller passed.
   *
   * A ref callback rather than an effect because an uncontrolled field's starting value is written by
   * whoever owns it — react-hook-form's `register`, or a plain `defaultValue` — and by the time the
   * node is attached that value is already there. There is no prop to watch for it.
   */
  const measureRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      // The caller's ref goes FIRST and the measurement second, which is the whole trick: react-hook-form
      // writes the field's initial value from inside its own ref callback, so measuring before handing
      // the node over reads an empty box on every `register`-driven field — the exact case this fixes.
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;

      if (node) setUncontrolledLength(node.value.length);
    },
    [ref]
  );

  // A controlled caller's own value is the count; re-render follows it for free.
  const isControlled = props.value !== undefined;
  const length = isControlled ? String(props.value ?? "").length : uncontrolledLength;

  // `defaultValue` can change on a remount-free reset (react-hook-form's `reset`, a modal reused for a
  // different record), and the node's value changes with it without an input event to notice.
  useEffect(() => {
    if (!isControlled && props.defaultValue !== undefined) {
      setUncontrolledLength(String(props.defaultValue ?? "").length);
    }
  }, [isControlled, props.defaultValue]);

  const errorClass = {
    "border-danger focus:border-danger": hasError,
  };

  const renderCharCount = () => {
    if (!hideCharCount && maxLength) {
      return <Typography variant="small" className="text-neutral-dark-gray">{charactersLeftLabel(maxLength, length)}</Typography>;
    }
  };

  return (
    <div>
      <Textarea
        ref={measureRef}
        className={clsx(
          "block w-full resize-none rounded-lg border-neutral-mid-gray px-4 text-base text-neutral-black placeholder-neutral-silver-gray focus:border-primary focus:ring-0 disabled:cursor-not-allowed disabled:bg-neutral-surface-gray",
          errorClass,
          className
        )}
        rows={rows}
        maxLength={maxLength}
        {...props}
        /* After the spread, and chaining the caller's handler rather than replacing it — the previous
           ordering meant a caller passing its own `onInput` silently switched the counter off. */
        onInput={(event) => {
          if (!isControlled) setUncontrolledLength((event.target as HTMLTextAreaElement).value.length);
          props.onInput?.(event);
        }}
      />
      {renderCharCount()}
    </div>
  );
});

TextArea.displayName = "TextArea";
